import { FC } from 'react'
import { useFormik } from 'formik'

import { SharedModalContent, SharedModalInputs, SharedModalTitle } from 'views/components/SharedModal'
import { useTranslation } from 'react-i18next'
import { FormGroup, Input } from '@agro-club/agroclub-shared'
import { apiClient } from 'modules/utils/httpClient'
import { endpoints } from 'modules/endpoints'
import { removeSeconds } from 'views/pages/LogisticsKR/helpers'
import { CommonUnloadWHForm } from 'views/pages/LogisticsKR/components/CommonUnloadWHForm'
import { addPrefixInErrors, removePropertyNamePrefix } from 'modules/utils/helpers'
import {
  isUnloadingWarehouse,
  LogisticsKRLoadingWarehouse,
  LogisticsKRUnloadingWarehouse,
} from 'views/pages/LogisticsKR/types'
import ADefaultModalFooter from 'views/components/DefaultModalFooter/ADefaultModalFooter'
import ASharedModal from 'views/components/SharedModal/ASharedModal'
import { AnalyticsPlaces } from 'views/pages/LogisticsKR/analyticsPlaces'
import { useAFormHandler } from 'analytics/hooks'

interface Props {
  setIsOpen: (val: boolean) => void
  onSuccess: () => void
  warehouse: LogisticsKRLoadingWarehouse | LogisticsKRUnloadingWarehouse
}

export const EditUnloadingModal: FC<Props> = ({ setIsOpen, onSuccess, warehouse }) => {
  const { t } = useTranslation('logisticsKR')
  const { formProgress, formHandler } = useAFormHandler()

  const formik = useFormik({
    initialValues: {
      unloading_warehouse_id: warehouse?.id,
      unloading_warehouse_address: warehouse?.address,
      unloading_warehouse_latitude: warehouse?.latitude,
      unloading_warehouse_longitude: warehouse?.longitude,
      unloading_warehouse_geo_object: warehouse?.geo_object,
      unloading_warehouse_unloading_type: isUnloadingWarehouse(warehouse) && warehouse?.unloading_type,
      unloading_warehouse_vehicle_type: warehouse?.vehicle_type,
      unloading_warehouse_schedule_from: removeSeconds(warehouse?.schedule_from),
      unloading_warehouse_volume_per_day: warehouse?.volume_per_day,
      unloading_warehouse_schedule_to: removeSeconds(warehouse?.schedule_to),
      unloading_warehouse_works_on_weekend: warehouse?.works_on_weekend,
      unloading_warehouse_car_delivery: warehouse?.car_delivery,
      unloading_warehouse_railway_delivery: warehouse?.railway_delivery,
      unloading_warehouse_is_dump_truck: isUnloadingWarehouse(warehouse) && warehouse?.is_dump_truck,
    },
    onSubmit: formHandler(
      async () =>
        await apiClient.put(
          endpoints.LogisticsKR.unloadingWarehouse(warehouse?.id),
          removePropertyNamePrefix(formik.values, /unloading_warehouse_/g),
        ),
      {
        onSuccess: () => {
          onSuccess()
          setIsOpen(false)
        },
        onError: (error) => {
          const { errors } = error
          formik.setErrors(addPrefixInErrors(errors, 'unloading_warehouse'))
        },
      },
    ),
  })

  return (
    <ASharedModal id={AnalyticsPlaces.WAREHOUSES.EDIT_MODAL} size="large" onClose={() => setIsOpen(false)}>
      <SharedModalTitle>{t('warehouses.forms.editHeading')}</SharedModalTitle>

      <SharedModalContent>
        <SharedModalInputs>
          <FormGroup
            error={formik.errors.unloading_warehouse_address}
            label={t('list.createForm.labels.address')}
            required
          >
            <Input
              placeholder={t('warehouses.forms.addressPlaceholder')}
              value={`${formik.values.unloading_warehouse_latitude} - ${formik.values.unloading_warehouse_longitude}`}
              disabled
            />
          </FormGroup>

          <FormGroup error={formik.errors.unloading_warehouse_address} label={t('warehouses.forms.textAddress')}>
            <Input
              placeholder={t('warehouses.forms.addressPlaceholder')}
              value={formik.values.unloading_warehouse_address}
              disabled
            />
          </FormGroup>

          <CommonUnloadWHForm formik={formik} />
        </SharedModalInputs>
      </SharedModalContent>

      <ADefaultModalFooter
        progress={formProgress}
        confirmButtonText={t('common:save')}
        cancelButtonText={t('common:cancel')}
        onConfirm={() => formik.handleSubmit()}
        onClose={() => setIsOpen(false)}
      />
    </ASharedModal>
  )
}
